<script setup lang="ts">
import { LOCALE } from "core/interfaces";
import { HEX_COLOR_REGEXP } from "shared/constants";
import { useField, useForm, useIsFormDirty, useIsFormValid } from "vee-validate";
import * as yup from "yup";

import { translationNormalize } from "~/common/helpers/translation-adapter";
import { ALERT_STATUS } from "~/common/interfaces/alert-status.enum";
import type { UpdateColorByIdDto } from "~/common/interfaces/dto/colors/update-color-by-id.dto";
import { useAlertStore } from "~/stores/alert";
import { useColorsStore } from "~/stores/colors";
import { useModalStore } from "~/stores/modal";

const { t } = useI18n();
const route = useRoute();
const alertStore = useAlertStore();
const colorsStore = useColorsStore();
const { colorsModalEdit } = useModalStore();
const { errors, handleSubmit } = useForm<UpdateColorByIdDto>({
	initialValues: {
		hex: colorsModalEdit.color.hex,
		...translationNormalize(colorsModalEdit.color.translations, ["name"])
	},
	validationSchema: yup.object({
		name: yup.object({
			uk: yup
				.string()
				.optional(),
			en: yup
				.string()
				.optional()
		}),
		hex: yup
			.string()
			.required(t("INPUT.hex.error.required"))
			.matches(HEX_COLOR_REGEXP, { message: t("INPUT.hex.error.hex") })
	})
});
const isFormDirty = useIsFormDirty();
const isFormValid = useIsFormValid();

const onSubmit = handleSubmit(async (dto: UpdateColorByIdDto) => {
	colorsStore.currentColor = await colorsStore.patchById(colorsModalEdit.color.id, dto);

	colorsModalEdit.closeModal();

	if (route.name === "components-colors") {
		colorsStore.getAll({}, colorsStore.recentGetAllQuery);
	}

	alertStore.addAlert({
		title: t("COMPONENTS.COLORS.MODAL_EDIT.alert.title"),
		description: t("COMPONENTS.COLORS.MODAL_EDIT.alert.description", { colorName: colorsModalEdit.color.name }),
		status: ALERT_STATUS.SUCCESS
	});
});

const { value: nameUK } = useField<string>("name.uk");
const { value: nameEN } = useField<string>("name.en");
const { value: hex } = useField<string>("hex");
</script>

<template>
	<AppModal
		:title="t('COMPONENTS.COLORS.MODAL_EDIT.title')"
		:close-button-text="t('COMMON.cancel')"
		:submit-button-text="t('COMMON.save')"
		:submit-button-active="isFormValid && isFormDirty"
		@close="colorsModalEdit.closeModal"
		@submit="onSubmit"
	>
		<span class="font-semibold text-base">
			{{ $t('COMMON.generalInfo') }}
		</span>
		<div class="mt-4">
			<AppInput
				v-model="hex"
				type="text"
				:title="$t('COMMON.hex')"
				:placeholder="$t('INPUT.PLACEHOLDER.hex')"
				:error="errors.hex"
				required
			/>
		</div>
		<div class="mt-6">
			<span class="font-semibold text-sm">
				Українською
			</span>
			<div class="mt-2 flex gap-x-2">
				<AppInput
					v-model="nameUK"
					type="text"
					:title="$t('COMMON.name')"
					:placeholder="$t('INPUT.PLACEHOLDER.color', 1, { locale: LOCALE.UKRAINIAN })"
					:error="errors['name.uk']"
				/>
			</div>
		</div>
		<div class="mt-4">
			<span class="font-semibold text-sm">
				English
			</span>
			<div class="mt-2 flex gap-x-2">
				<AppInput
					v-model="nameEN"
					type="text"
					:title="$t('COMMON.name')"
					:placeholder="$t('INPUT.PLACEHOLDER.color', 1, { locale: LOCALE.ENGLISH })"
					:error="errors['name.en']"
				/>
			</div>
		</div>
	</AppModal>
</template>
